/**
 * Maps stored survey answers to their display labels. The values are the enum
 * names the shared onboarding mapper expects, so they stay aligned with the
 * survey screens rather than the API integers.
 */
import type { SurveyState } from '@wlb/shared/types/survey';

type Translate = (key: string, values?: Record<string, string | number>) => string;

export interface PortraitAnswer {
  key: string;
  question: string;
  answer: string;
}

export interface PortraitRating {
  key: string;
  question: string;
  leftLabel: string;
  rightLabel: string;
  value: number;
}

const NOT_ANSWERED = '—';

const LABELS: Record<string, Record<string, string>> = {
  mainGoal: {
    LoseWeight: 'survey.q1.loseWeight',
    StayConsistent: 'survey.q1.stayConsistent',
    BuildMuscle: 'survey.q1.stayConsistent',
    GetHealthier: 'survey.q1.getHealthier',
    MaintainWeight: 'survey.q1.maintainWeight',
  },
  commitmentLevel: {
    VeryCommitted: 'survey.q3.veryCommitted',
    PrettyCommitted: 'survey.q3.prettyCommitted',
    GetStarted: 'survey.q3.gettingStarted',
  },
  buddyType: {
    mentor: 'survey.q4.mentor',
    partner: 'survey.q4.partner',
    cheerleader: 'survey.q4.cheerleader',
  },
  communicationStyle: {
    supportive: 'survey.q5.supportive',
    balanced: 'survey.q5.balanced',
    direct: 'survey.q5.direct',
  },
  checkInFrequency: {
    daily: 'survey.q6.daily',
    fewTimes: 'survey.q6.fewTimes',
    onceWeek: 'survey.q6.onceWeek',
  },
  genderPreference: {
    noPreference: 'survey.q7.noPreference',
    male: 'survey.q7.male',
    female: 'survey.q7.female',
  },
};

const label = (group: string, value: string | null | undefined, t: Translate): string => {
  if (!value) return NOT_ANSWERED;
  const key = LABELS[group]?.[value];
  return key ? t(key) : value;
};

/** The six readiness sliders, in the order the survey asks them. */
const RATING_KEYS = [
  { key: 'motivation', suffix: 'q1' },
  { key: 'consistency', suffix: 'q2' },
  { key: 'accountability', suffix: 'q3' },
  { key: 'sharingHonesty', suffix: 'q4' },
  { key: 'checkInImportance', suffix: 'q5' },
  { key: 'emotionalSupport', suffix: 'q6' },
] as const;

export const buildJourneyAnswers = (survey: SurveyState, t: Translate): PortraitAnswer[] => [
  { key: 'mainGoal', question: t('survey.q1.question'), answer: label('mainGoal', survey.mainGoal, t) },
  {
    key: 'poundsToLose',
    question: t('survey.q2.question'),
    answer:
      survey.poundsToLose == null
        ? NOT_ANSWERED
        : t('survey.q9.poundsToLoseLabel', { count: survey.poundsToLose }),
  },
  { key: 'commitmentLevel', question: t('survey.q3.question'), answer: label('commitmentLevel', survey.commitmentLevel, t) },
  { key: 'buddyType', question: t('survey.q4.question'), answer: label('buddyType', survey.buddyType, t) },
  { key: 'communicationStyle', question: t('survey.q5.question'), answer: label('communicationStyle', survey.communicationStyle, t) },
  { key: 'checkInFrequency', question: t('survey.q6.question'), answer: label('checkInFrequency', survey.checkInFrequency, t) },
  { key: 'genderPreference', question: t('survey.q7.question'), answer: label('genderPreference', survey.genderPreference, t) },
  {
    key: 'ageRange',
    question: t('survey.q8.questionPortrait'),
    answer: survey.ageRange
      ? t('survey.q9.ageRangeLabel', { min: survey.ageRange.min, max: survey.ageRange.max })
      : NOT_ANSWERED,
  },
];

export const buildReadinessRatings = (survey: SurveyState, t: Translate): PortraitRating[] =>
  RATING_KEYS.map(({ key, suffix }) => ({
    key,
    question: t(`survey.q9.${suffix}`),
    leftLabel: t(`survey.q9.${suffix}Left`),
    rightLabel: t(`survey.q9.${suffix}Right`),
    value: survey.personalizationRatings?.[key] ?? 0,
  }));

/** True when the member has not answered anything yet. */
export const isPortraitEmpty = (survey: SurveyState): boolean =>
  Object.values(survey).every(value => value == null);
